import { useRef, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (signatureData: string) => void;
  loading?: boolean;
};

export default function SignatureDialog({
  open,
  onClose,
  onConfirm,
  loading = false,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [context, setContext] = useState<CanvasRenderingContext2D | null>(null);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    if (open) {
      // Delay to ensure canvas is mounted and dimensions are correct
      setTimeout(() => {
        const canvas = canvasRef.current;
        if (canvas) {
          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.lineWidth = 2;
            ctx.lineCap = "round";
            ctx.strokeStyle = "black";
            setContext(ctx);
          }
        }
      }, 100);
    } else {
      setHasSignature(false);
      setIsDrawing(false);
    }
  }, [open]);

  const startDrawing = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!context) return;
    setIsDrawing(true);
    const { offsetX, offsetY } = event.nativeEvent;
    context.beginPath();
    context.moveTo(offsetX, offsetY);
  };

  const draw = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !context) return;
    const { offsetX, offsetY } = event.nativeEvent;
    context.lineTo(offsetX, offsetY);
    context.stroke();
    if (!hasSignature) setHasSignature(true);
  };

  const endDrawing = () => {
    setIsDrawing(false);
    if (context) {
      context.closePath();
    }
  };

  const clearCanvas = () => {
    if (canvasRef.current && context) {
      context.clearRect(
        0,
        0,
        canvasRef.current.width,
        canvasRef.current.height
      );
      setHasSignature(false);
    }
  };

  const handleConfirm = () => {
    if (canvasRef.current) {
      const signatureData = canvasRef.current.toDataURL("image/png");
      onConfirm(signatureData);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Sign Off Clinical Record</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            mt: 2,
          }}
        >
          <canvas
            ref={canvasRef}
            width={500}
            height={200}
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              cursor: "crosshair",
              touchAction: "none",
              backgroundColor: "#fff",
            }}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={endDrawing}
            onMouseOut={endDrawing}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={clearCanvas} color="inherit" disabled={!hasSignature}>
          Clear
        </Button>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <LoadingButton
          onClick={handleConfirm}
          variant="contained"
          loading={loading}
          disabled={!hasSignature}
        >
          Sign & Submit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
